import { timeoutFetchSentryGuard } from 'Fetch';
import {
  getUser,
  getUserLogOut,
  getUserSession,
  postCreateEnquiry,
  postCreateTrackerNewUser,
  postUserUpdateInfo,
} from 'PrivateAPI';
import { postVrmCheck } from 'PublicAPI';

import { ConfigStateStatic } from 'Context/config';
import { establishCognitoSession, UserDispatch, UserStateStatic } from 'Context/user';
import { VehicleLookupDispatch } from 'Context/vehicleLookup';
import { VehicleDispatch } from 'Context/vehicles';

import { GA_VARIABLES, SNOWPLOW_GET_TRACKERS, VALUATIONS_GA_TAGS } from 'Utilities/analytics';
import setGAData from 'Utilities/analytics/setGAData';
import { postBroadcast } from 'Utilities/broadcastChannel';
import { getStrippedUserData, trackUserCookie } from 'Utilities/helpers';
import { safeLocalStorage } from 'Utilities/storages';
import { VRM_CHECK_CODES } from 'Utilities/vrm';

import { checkMarketPlaceEligibility as checkMarketPlaceEligibilityPartnerId } from './useSetGaMarketplaceEligible';

const ENQUIRY_SOURCE = 'seller-webapp';

const checkCookiesMisuse = async (userId) => {
  const sessionUserId = safeLocalStorage.getItem('sessionUserId') || {};
  const { tokenExp, tokenId, userId: previousUserId } = sessionUserId;

  if (previousUserId && Number.isInteger(Number(previousUserId)) && Number(previousUserId) !== Number(userId)) {
    window?.Sentry?.captureException(new Error('Potential cookies misuse (extended check)'), {
      extra: {
        previousUserId,
        sessionUserId,
        tokenExp,
        tokenId,
        userId,
      },
      level: 'warning',
    });
  }
};

const addVehicleWithEnquiry = ({ enquiry, vehicle }) => {
  VehicleDispatch.actions.addWithEnquiry({ enquiry, vehicle });
};

const addVehicleLookup = ({ enquiry, resultError, vehicle }) => {
  VehicleLookupDispatch.actions.add({ enquiry, resultError, vehicle });
};

const updateUser = (user) => {
  const { vehicles, ...seller } = user;
  UserDispatch.actions.update(seller);
};

const updateVehicles = (user) => {
  if (user?.vehicles) {
    VehicleDispatch.actions.replace(user.vehicles);
  }
};

const updateLookupContext = (user, vehicle) => {
  const updatedVehicleWithEnquiry = user?.vehicles?.find((v) => v.id === vehicle.id);
  if (updatedVehicleWithEnquiry) {
    addVehicleLookup({
      enquiry: updatedVehicleWithEnquiry?.enquiry,
      vehicle: updatedVehicleWithEnquiry,
    });
  }
};

const refetchUser = async (user) => {
  if (Number.isInteger(user?.id)) {
    try {
      const updatedUser = await getUser({ include: 'tracker' });
      return updatedUser;
    } catch (err) {
      timeoutFetchSentryGuard(err, () => window?.Sentry?.captureException?.(new Error(err), {
        extra: {
          user: getStrippedUserData(user),
        },
        tags: {
          fetch: 'Get user and their vehicles',
        },
      }));
    }
  }
  return user;
};

const setCrmPreferences = (consent) => (consent ? 'marketing' : 'service');

// Hit `/vrm-check`
// Update `Context/vehicleLookup`
// Update `Context/user`
export const vrmCheckAction = async (vrmValue, isRefreshLookupVehicleAfterFetch = true) => {
  const {
    code,
    customer,
    enquiry,
    error: apiError,
    vehicle,
  } = await postVrmCheck(vrmValue)
    .catch((error) => ({ error }));

  const result = {
    customer,
    enquiry,
    error: apiError || code,
    vehicle,
  };

  if (result.error) {
    const errorMsg = result.error?.code || result.error?.message;
    const msg = Object.values(VRM_CHECK_CODES).includes(errorMsg)
      ? errorMsg
      : VRM_CHECK_CODES.BAD_REQUEST;
    result.error = msg;
    addVehicleLookup({ resultError: { msg, vrm: vrmValue } });
    return result;
  }

  if (vehicle) {
    addVehicleLookup({
      enquiry,
      vehicle,
    });
  }

  if (customer) {
    updateUser(customer);
    const userData = await refetchUser(customer);
    updateVehicles(userData);

    if (!enquiry?.expired && isRefreshLookupVehicleAfterFetch) {
      updateLookupContext(userData, vehicle);
    }
  }

  return result;
  // Uncomment to test vrm check returning an enquiry.expired date
  // return { ...result, enquiry: { ...result.enquiry, expired: '2020-11-14T15:21:08.566Z' } };
};

const getSnowplowIds = () => {
  const snowplowNetworkUserId = document.cookie.split('; ').find((cookie) => cookie.startsWith('sp='))?.split('=')[1];

  let snowplowDomainUserId;
  let snowplowSessionId;
  SNOWPLOW_GET_TRACKERS(function getTrackers() {
    const firstTracker = Object.values(this)[0];
    [, snowplowDomainUserId, , , , , snowplowSessionId] = firstTracker?.getDomainUserInfo() || [];
  });

  return { snowplowDomainUserId, snowplowNetworkUserId, snowplowSessionId };
};

// Hit `/create-enquiry`
// Update `Context/vehicleLookup`
// Update `Context/vehicles`
// Update `Context/user`
export const createEnquiryAction = async ({
  consent,
  email,
  mileage,
  name,
  phone,
  postcode,
  sale_timeframe, // eslint-disable-line camelcase
  timeframe,
  vrm,
}) => {
  if (UserStateStatic?.state?.id) {
    checkCookiesMisuse(UserStateStatic.state.id);
  }

  const { state: { affiliate } } = ConfigStateStatic;

  const query = { include: 'tracker' };

  const {
    code,
    customer,
    enquiry,
    error: apiError,
    message,
    vehicle,
  } = await postCreateEnquiry({
    affiliate: affiliate?.slug || null,
    app_variant: {}, // TODO - Put `app_variant` into store
    consent,
    email,
    emailCrmTier: setCrmPreferences(consent?.consent),
    mileage,
    name,
    phone,
    postcode,
    sale_timeframe: sale_timeframe || timeframe, // eslint-disable-line camelcase
    source: ENQUIRY_SOURCE,
    vrm,
    ...getSnowplowIds(),
  }, query).catch((error) => {
    timeoutFetchSentryGuard(error, () => window?.Sentry?.captureException(new Error(error), {
      extra: {
        vrm,
      },
      tags: {
        fetch: 'Create enquiry error',
      },
    }));
    return error;
  });

  const result = {
    customer,
    enquiry,
    error: apiError || code || message,
    vehicle,
  };

  if (result.error) { // API Error
    return result;
  }

  if (vehicle) {
    const payload = {
      enquiry,
      vehicle,
    };

    addVehicleWithEnquiry(payload);
    addVehicleLookup(payload);

    if (enquiry?.id) {
      GA_VARIABLES.enquiryId = enquiry.id;
      GA_VARIABLES.userId = customer?.id;
      GA_VARIABLES.vin = 'N/A';
      GA_VARIABLES.vrm = vehicle?.vrm;
      checkMarketPlaceEligibilityPartnerId({ enquiry, marketplaceEligible: enquiry?.marketplaceEligible });
      VALUATIONS_GA_TAGS.ENQUIRY_CREATED(enquiry);

      const strippedDataLayer = [...globalThis?.dataLayer || []].reduce((acc, val) => {
        const pii = ['phone_number', 'email', 'postcode'];

        if (typeof val === 'object' && !pii.includes(Object.keys(val)[0])) {
          acc.push(val);
        }

        return acc;
      }, []);

      if (!strippedDataLayer.some((v) => Object.keys(v)[0] === 'enquiryId')) {
        window?.Sentry?.captureException?.(new Error('createEnquiryAction dataLayer is missing an enquiryID'), {
          extra: {
            customer: getStrippedUserData(customer),
            enquiry,
            strippedDataLayer,
            vehicle,
          },
          level: 'warning',
        });
      }
    }
  }

  if (Number.isInteger(customer?.id)) {
    updateUser(customer);
    updateVehicles(customer);
    await getUserSession({ token: customer.login_token });
    trackUserCookie(customer);

    // Need to set the auth token above for this call to succeed
    if (enquiry?.id) {
      setGAData({ enquiry });
    }
  } else {
    window?.Sentry?.captureException?.(new Error('createEnquiryAction seller ID is not an integer'), {
      extra: {
        customer: getStrippedUserData(customer),
      },
    });
  }

  return result;
};

export const updateUserInfoAction = async (payload) => {
  const { state: { featureFlags } } = ConfigStateStatic;

  const { showCognitoLogin } = featureFlags || {};

  if (UserStateStatic?.state?.id) {
    checkCookiesMisuse(UserStateStatic.state.id);
  }

  const useCognito = showCognitoLogin && UserStateStatic.state.cognitoId !== null;

  const {
    changed,
    code,
    customer,
    error: apiError,
    message,
  } = await postUserUpdateInfo(payload)
    .catch((error) => ({ error }));

  const result = {
    changed,
    customer,
    error: apiError || code,
    message,
  };

  if (result.error) { // API Error
    window?.Sentry?.captureException?.(new Error(apiError));
    return result;
  }

  if (Number.isInteger(customer?.id)) {
    updateUser(customer);
  }

  if (['auth_token', 'login_token', 'email'].some((val) => result.changed?.[val])) {
    await getUserLogOut();
  }

  if (useCognito) {
    const { error: loginError, success: loginSuccess } = await establishCognitoSession(
      UserStateStatic.state.cognitoTokens,
    );
    if (loginSuccess) {
      postBroadcast();
    } else {
      const error = loginError || new Error('Cognito Login unsuccessful');
      result.error = error;
      window?.Sentry?.captureException?.(new Error(error));
    }
    return result;
  }

  const { error: sessionError } = await getUserSession({ token: customer.login_token })
    .catch((error) => ({ error: error?.message || error }));

  if (sessionError) {
    result.message = sessionError?.toString?.() || '';
    result.error = sessionError;

    window?.Sentry?.captureException?.(new Error(sessionError));
  } else {
    trackUserCookie(customer);
    postBroadcast();
  }

  return result;
};

export const createTrackerEnquiryAction = async ({
  consent,
  email,
  name,
  phone,
}) => {
  const {
    data: customer, status, statusMessage,
  } = await postCreateTrackerNewUser({
    consent,
    email,
    emailCrmTier: setCrmPreferences(consent?.consent),
    name,
    phone,
  }).catch((error) => ({ error }));
  const result = {
    customer,
    error: (status === 'failure') && { message: statusMessage },
    status,
    statusMessage,
  };
  if (result.error) {
    return result;
  }

  if (customer) {
    updateUser(customer);
    await getUserSession({ token: customer.login_token });
    trackUserCookie(customer);
  }

  return result;
};

export const removeUser = async () => {
  await getUserLogOut()
    .catch((err) => {
      throw new Error(err);
    });

  window.location.href = '/'; // Force a page load
};
